import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import {
  ModalContainer,
  CloseButton,
  ModalContent,
  ModalTitle,
  ButtonGroup,
  CancelButton,
  ConfirmButton,
  Row,
  ButtonRow,
  InfoIconWrapper,
  TooltipContainer,
  TooltipText,
  IconWrapper,
  Content,
  ContentContainer,
  EmptyState,
  Column,
  Container,
  PaginationWrapper,
  CheckboxContainer,
} from "./ExtendVideoModal.styles";
import Modal from "../utils/Modal/Modal";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { ReactComponent as InfoIcon } from "../../assets/icons/info-circle.svg";
import { useMediaQuery } from "react-responsive";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as BlackStarSvg } from "../../assets/icons/blackStar.svg";

import {
  selectCategories,
  selectCollectionDetails,
  selectExploreCollections,
  selectExploreCollectionsFilter,
  selectFavorites,
  selectFollowing,
  selectGenerateCollection,
  setSelectedExploreCollectionFilter,
} from "../../store/slices/model/slice";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchCollection,
  getExploreCollections,
  getUserFavorites,
  getUserFollowing,
} from "../../store/slices/model/asyncThunks";
import debounce from "lodash.debounce";
import CollectionFilter from "../CollectionFilter/CollectionFilter";
import VideoMasonrySkeletonLoader from "../utils/VideoMasonrySkeletonLoader";
import ExploreItem from "../ExploreItem/ExploreItem";
import { setSignUpModalOpen } from "../../store/slices/common/slice";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import Pagination from "../Pagination/Pagination";
import { isImageOutput } from "../../utils/utils";
import { BLEND_MODEL, MEDIA_TYPE } from "../../utils/constants";
import LottieIcon from "../../components/LottieIcon/LottieIcon";
import checkboxAnimation from "../../assets/animations/radioButton.json";
import settingsAnimation from "../../assets/animations/settings.json";

const ExtendVideoModal = ({
  isOpen,
  onClose,
  onConfirm,
  confirmButtonType = "primary",
  setIsEnterPromptModalOpen,
  setSelectedModel,
  setExtendVideoPrice,
  selectedCollectionVideo,
  totalPriceVideoExtend,
  setSelectedExtendCollection,
}) => {
  const dispatch = useDispatch();
  const [showTooltip, setShowTooltip] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [page, setPageState] = useState(1);
  const [search, setSearch] = useState("");
  const [searchValue, setSearchValue] = useState(() => "");

  const filter = useSelector(selectExploreCollectionsFilter);
  const exploreData = useSelector(selectExploreCollections);
  const favoriteData = useSelector(selectFavorites);
  const loggedIn = useSelector(selectLoggedIn);
  const { data: categories } = useSelector(selectCategories);
  const followingData = useSelector(selectFollowing);

  const isDesktop = useMediaQuery({ maxWidth: 1224 });
  const isTable = useMediaQuery({ maxWidth: 950 });
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const contentContainerRef = useRef(null);
  const { loading: isLoading } = useSelector(selectGenerateCollection);
  const { data: collectionDetailsData, loading: extendLoading } = useSelector(
    selectCollectionDetails,
  );

  useLockBodyScroll(isOpen);

  useEffect(() => {
    if (categories?.length && !filter) {
      dispatch(setSelectedExploreCollectionFilter(categories[0]._id));
    }

    if (!categories?.length) {
      dispatch(
        getExploreCollections({
          page: 1,
          search: "",
        }),
      );
    }
  }, [categories, dispatch, filter]);

  useEffect(() => {
    if (
      !loggedIn &&
      (filter === "FAVORITES" || filter === "FOLLOWING") &&
      categories.length
    ) {
      dispatch(setSelectedExploreCollectionFilter(categories[0]._id));
    }
  }, [loggedIn, filter, categories, dispatch]);

  const onChangeFilter = (newFilter) => {
    dispatch(setSelectedExploreCollectionFilter(newFilter));
    setPageState(1);

    if (newFilter === "FAVORITES") {
      dispatch(getUserFavorites({ page: 1, search }));
    } else if (newFilter === "FOLLOWING") {
      dispatch(getUserFollowing({ page: 1, search }));
    } else if (newFilter === "EXTEND_WITH") {
      if (selectedCollectionVideo?._id) {
        dispatch(
          fetchCollection({ collectionId: selectedCollectionVideo._id }),
        );
      }
    } else {
      dispatch(
        getExploreCollections({ page: 1, search, categoryId: newFilter }),
      );
    }
  };

  const numOfColumns = useMemo(() => {
    switch (true) {
      case isMobile:
        return 2;
      case isTable:
        return 3;
      case isDesktop:
        return 4;
      default:
        return 5;
    }
  }, [isDesktop, isTable, isMobile]);

  const data = useMemo(() => {
    if (filter === "FAVORITES") return favoriteData?.data || [];
    if (filter === "FOLLOWING") return followingData?.data || [];
    if (filter === "EXTEND_WITH") {
      const extend = collectionDetailsData?.extend || [];
      const query = search.trim().toLowerCase();
      if (!query) return extend;
      return extend.filter((item) => {
        const name = item?.name?.toLowerCase() || "";
        const username = item?.username?.toLowerCase() || "";
        const tags = Array.isArray(item?.tags)
          ? item.tags.map((t) => t?.name?.toLowerCase() || "")
          : [];
        return (
          name.includes(query) ||
          username.includes(query) ||
          tags.some((t) => t.includes(query))
        );
      });
    }
    return exploreData?.data || [];
  }, [
    filter,
    favoriteData,
    followingData,
    exploreData,
    collectionDetailsData,
    search,
  ]);

  const total = useMemo(() => {
    if (filter === "FAVORITES") return favoriteData?.total || 0;
    if (filter === "FOLLOWING") return followingData?.total || 0;
    if (filter === "EXTEND_WITH") return data.length;
    return exploreData?.total || 0;
  }, [filter, favoriteData, followingData, exploreData, data]);

  const loading = useMemo(() => {
    if (filter === "FAVORITES") return favoriteData.loading;
    if (filter === "FOLLOWING") return followingData.loading;
    if (filter === "EXTEND_WITH") return extendLoading;
    return exploreData.loading;
  }, [filter, favoriteData, followingData, exploreData, extendLoading]);

  const changeHandler = useCallback(
    (text) => {
      setPageState(1);
      if (search !== text) {
        setSearch(text);
        if (filter === "EXTEND_WITH") {
          return;
        }
        if (filter === "FAVORITES") {
          dispatch(getUserFavorites({ page: 1, search: text }));
        } else if (filter === "FOLLOWING") {
          dispatch(getUserFollowing({ page: 1, search: text }));
        } else {
          dispatch(
            getExploreCollections({
              page: 1,
              search: text,
              categoryId: filter,
            }),
          );
        }
      }
    },
    [dispatch, filter, search],
  );

  const debouncedChangeHandler = useMemo(
    () => debounce(changeHandler, 300),
    [changeHandler],
  );

  const setPage = (newPage) => {
    if (contentContainerRef.current) {
      contentContainerRef.current.scrollTo({
        top: 0,
        behavior: "instant",
      });
    }

    setPageState(newPage);
    if (filter === "FAVORITES") {
      dispatch(getUserFavorites({ page: newPage, search }));
    } else if (filter === "FOLLOWING") {
      dispatch(getUserFollowing({ page: newPage, search }));
    } else {
      dispatch(
        getExploreCollections({ page: newPage, search, categoryId: filter }),
      );
    }
  };

  useEffect(() => {
    if (filter === "FAVORITES" && !data) {
      setPageState(1);
      dispatch(
        getUserFavorites({
          page: 1,
          search: "",
        }),
      );
    }
  }, [filter, dispatch, data]);

  useEffect(() => {
    if (filter === "FOLLOWING" && !data) {
      setPageState(1);
      dispatch(
        getUserFollowing({
          page: 1,
          search: "",
        }),
      );
    }
  }, [filter, dispatch, data]);

  useEffect(() => {
    if (!filter || data) return;

    if (filter !== "FAVORITES" && filter !== "FOLLOWING") {
      setPageState(1);

      dispatch(
        getExploreCollections({
          page: 1,
          search: "",
          categoryId: filter,
        }),
      );
    }
  }, [dispatch, filter, data]);

  useEffect(() => {
    if (filter === "EXTEND_WITH" && data.length === 0 && isOpen) {
      const trendingCategory = categories?.find((c) => c.name === "Trending");
      const fallbackFilter = trendingCategory?._id ?? categories?.[0]?._id;
      if (fallbackFilter) {
        dispatch(setSelectedExploreCollectionFilter(fallbackFilter));
        dispatch(
          getExploreCollections({
            page: 1,
            search: "",
            categoryId: fallbackFilter,
          }),
        );
      }
    }
  }, [filter, data, categories, dispatch, search, isOpen]);

  const resetStates = () => {
    setPageState(1);
    setSearchValue("");

    if (categories?.length) {
      const defaultFilter = categories[0]._id;
      dispatch(setSelectedExploreCollectionFilter(defaultFilter));

      dispatch(
        getExploreCollections({
          page: 1,
          search: "",
          categoryId: defaultFilter,
        }),
      );
    }
  };

  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    if (!isLoading) {
      onClose();
      resetStates();
    }
  };

  const isImage = selectedCollectionVideo?.output?.type === MEDIA_TYPE.IMAGE;

  if (!selectedCollectionVideo) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      shouldCloseOnOverlayClick={false}
      customOverlayStyles={{ zIndex: 3100 }}
    >
      <ModalContainer>
        <ModalContent>
          <Row>
            <ModalTitle>
              {isImage ? "Animate Image" : "Extend Video"}
            </ModalTitle>
            <CloseButton onClick={handleCancel}>
              <CloseSvg width={20} height={20} />
            </CloseButton>
          </Row>

          <CollectionFilter
            minHeight
            offHorizontalPadding
            searchPlaceholderText="title, username or tags"
            filter={filter}
            setFilter={onChangeFilter}
            searchValue={searchValue}
            onSearchChange={(value) => {
              setSearchValue(value);
              debouncedChangeHandler(value);
            }}
            hidePlaylist
            hidePicturesCategory
            isExtendModal
            extendData={collectionDetailsData?.extend.length > 0}
          />
          <Container>
            <ContentContainer ref={contentContainerRef}>
              {loading || !data ? (
                <VideoMasonrySkeletonLoader count={24} mobileTwoRows />
              ) : (
                <Content>
                  {!data.length ? (
                    <EmptyState>
                      <h3>No videos found</h3>
                      <p>There are no videos to display at the moment.</p>
                    </EmptyState>
                  ) : (
                    Array.from({ length: numOfColumns }).map((_, colIndex) => {
                      return (
                        <Column key={colIndex}>
                          {data
                            .filter(
                              (item) =>
                                !isImageOutput(item.modelName) &&
                                item.modelType !== BLEND_MODEL,
                            )
                            .filter((_, i) => i % numOfColumns === colIndex)
                            .map((item) => {
                              const isSelected = selectedVideoId === item._id;

                              return (
                                <ExploreItem
                                  key={item._id}
                                  item={item}
                                  offGenerate
                                  onItemClick={() => {
                                    if (loggedIn) {
                                      if (isSelected) {
                                        setSelectedVideoId(null);
                                        setSelectedModel(null);
                                        setSelectedExtendCollection(null);
                                      } else {
                                        setSelectedVideoId(item._id);
                                        setSelectedModel(item.model);
                                        setExtendVideoPrice(item.price);
                                        setSelectedExtendCollection(item);
                                      }
                                    } else {
                                      dispatch(setSignUpModalOpen(true));
                                    }
                                  }}
                                  extraContent={
                                    <CheckboxContainer>
                                      <LottieIcon
                                        animationData={checkboxAnimation}
                                        trigger="toggle-reverse"
                                        isActive={isSelected}
                                        strokeColor={"#fff"}
                                        size={24}
                                        speed={2}
                                      />
                                    </CheckboxContainer>
                                  }
                                />
                              );
                            })}
                        </Column>
                      );
                    })
                  )}
                </Content>
              )}
            </ContentContainer>
            {filter !== "EXTEND_WITH" && (
              <PaginationWrapper>
                <Pagination
                  small={isMobile}
                  currentPage={page}
                  totalCount={total}
                  perPage={30}
                  onPageChange={(page) => setPage(page)}
                />
              </PaginationWrapper>
            )}
          </Container>

          <ButtonGroup>
            <ButtonRow>
              <IconWrapper
                onClick={() => {
                  setIsEnterPromptModalOpen(true);
                  localStorage.setItem("wasPromptModalOpen", true);
                }}
              >
                <LottieIcon
                  animationData={settingsAnimation}
                  trigger="hover"
                  size={24}
                  reverseSpeed={4}
                  strokeColor={"white"}
                />
              </IconWrapper>
              <ConfirmButton
                onClick={handleConfirm}
                $type={confirmButtonType}
                disabled={isLoading || !selectedVideoId}
              >
                {isLoading ? (
                  <l-line-spinner size="20" stroke="2" speed="1" color="#000" />
                ) : (
                  <>
                    Generate
                    {totalPriceVideoExtend && (
                      <>
                        <BlackStarSvg width={14} height={14} />{" "}
                        {totalPriceVideoExtend}
                      </>
                    )}
                  </>
                )}
              </ConfirmButton>
              <InfoIconWrapper
                onMouseEnter={() => {
                  if (!isMobile) {
                    setShowTooltip(true);
                  }
                }}
                onMouseLeave={() => {
                  if (!isMobile) {
                    setShowTooltip(false);
                  }
                }}
                onClick={() => {
                  if (isMobile) {
                    setShowTooltip(!showTooltip);
                  }
                }}
              >
                <InfoIcon width={24} height={24} />
                {showTooltip && (
                  <TooltipContainer>
                    <TooltipText>
                      {isImage
                        ? "The generated image will be used as the first frame of the generated video. For best results, match the original image. The AI follows basic physics — objects not present in the input image are unlikely to appear in the generated video."
                        : "Extending a video reuses its last frame as the starting point for the new generation, making the final video longer and seamless."}
                    </TooltipText>
                  </TooltipContainer>
                )}
              </InfoIconWrapper>
            </ButtonRow>
            <CancelButton onClick={handleCancel} disabled={isLoading}>
              Cancel
            </CancelButton>
          </ButtonGroup>
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
};

export default ExtendVideoModal;
